import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';

interface Document {
  id: string;
  name: string;
  type: string;
  file_url: string;
  status: string;
  uploaded_at: string;
  admin_notes?: string;
}

const DocumentsPage: React.FC = () => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [reuploadingId, setReuploadingId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) return;

      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('user_id', session.user.id)
        .order('uploaded_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (fileUrl: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('carrier-documents')
        .createSignedUrl(fileUrl, 3600);

      if (error || !data?.signedUrl) {
        alert('Unable to load document');
        return;
      }
      window.open(data.signedUrl, '_blank');
    } catch (err) {
      console.error('Error viewing document:', err);
      alert('Unable to load document');
    }
  };

  const handleReuploadClick = (docId: string) => {
    setReuploadingId(docId);
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !reuploadingId) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) return;

      const doc = documents.find(d => d.id === reuploadingId);
      if (!doc) return;

      // Upload new file
      const filePath = `${session.user.id}/${doc.type}_${Date.now()}_${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('carrier-documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Update the document record — new file, reset to pending
      const { error: updateError } = await supabase
        .from('documents')
        .update({
          name: file.name,
          file_url: filePath,
          file_size: file.size,
          file_type: file.type,
          status: 'pending',
          uploaded_at: new Date().toISOString(),
          admin_notes: null,
          reviewed_at: null,
          reviewed_by: null,
        })
        .eq('id', reuploadingId);

      if (updateError) throw updateError;

      // Notify broker
      const { data: profileData } = await supabase
        .from('profiles')
        .select('company_code, first_name, last_name')
        .eq('id', session.user.id)
        .single();

      if (profileData?.company_code) {
        const carrierName = `${profileData.first_name} ${profileData.last_name}`.trim();
        await supabase.from('notifications').insert({
          company_code: profileData.company_code,
          title: 'Document Re-uploaded',
          message: `${carrierName} re-uploaded their ${doc.type?.toUpperCase() || 'document'} for review`,
          type: 'docs_submitted',
        });
      }

      alert('Document re-uploaded successfully!');
      fetchDocuments();
    } catch (err) {
      console.error('Error re-uploading:', err);
      alert('Failed to re-upload. Please try again.');
    } finally {
      setReuploadingId(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'verified':
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'verified':
      case 'approved':
        return '✓';
      case 'rejected':
        return '✗';
      default:
        return '⏳';
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return <div className="text-center py-8">Loading documents...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">My Documents</h1>
        <p className="text-gray-600">Documents submitted during onboarding</p>
      </div>

      {/* Hidden file input for re-upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        className="hidden"
        onChange={handleFileSelected}
      />

      {documents.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500">No documents uploaded yet.</p>
          <p className="text-sm text-gray-400 mt-2">
            Documents will appear here after you complete onboarding.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow divide-y">
          {documents.map((doc) => (
            <div key={doc.id} className="p-4 hover:bg-gray-50">
              <div className="flex justify-between items-center">
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{doc.name}</p>
                  <p className="text-sm text-gray-500">
                    {doc.type?.toUpperCase()} • Uploaded {formatDate(doc.uploaded_at)}
                  </p>
                  {doc.admin_notes && (
                    <div className="mt-2 p-2 bg-red-50 rounded border border-red-200">
                      <p className="text-xs font-medium text-red-700">Broker Notes:</p>
                      <p className="text-sm text-red-600">{doc.admin_notes}</p>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(doc.status)}`}>
                    {getStatusIcon(doc.status)} {doc.status?.charAt(0).toUpperCase() + doc.status?.slice(1)}
                  </span>
                  {doc.file_url && (
                    <button
                      onClick={() => handleView(doc.file_url)}
                      className="px-3 py-1.5 text-xs bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 font-medium"
                    >
                      View
                    </button>
                  )}
                  {doc.status === 'rejected' && (
                    <button
                      onClick={() => handleReuploadClick(doc.id)}
                      className="px-3 py-1.5 text-xs bg-orange-50 text-orange-700 rounded-md hover:bg-orange-100 font-medium"
                    >
                      Re-upload
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DocumentsPage;